import ModalKeyboardAwareScrollView from '-/Custom/ModalKeyboardAwareScrollView';
import ModalKeyboardStickyView from '-/Custom/ModalKeyboardStickyView';
import {
  BackgroundContainer,
  BackgroundFilter,
  EventBannerFragment,
} from '-/Event/EventBanner';
import EventBannerBackground from '-/Event/EventBanner/EventBannerBackground';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useFormContext, useWatch } from 'react-hook-form';
import { useMutation, useQuery } from 'urql';
import FormSaveButton from '@/components/Custom/FormSaveButton';
import EventBannerItem, {
  EventBannerItemFragment,
} from '@/components/Event/EventBanner/EventBannerItem';
import type { EventScorekeepingScoreFormValues } from '@/components/Event/EventScorekeeping/EventScorekeepingScore/EventScorekeepingScore.types';
import Box from '@/components/UI/Box';
import FormContentBoundary from '@/components/UI/FormContentBoundary';
import { graphql, readFragment } from '@/gql/graphql';
import { selectionAsync } from '@/utils/Haptics';
import EventScorekeepingScoreInput from './components/EventScorekeepingScoreInput';
import EventStatusSelect from './components/EventStatusSelect';

const GetEventScorekeepingScore = graphql(/* GraphQL */ `
  query GetEventScorekeepingScore($eventId: uuid!) {
    event_by_pk(id: $eventId) {
      id
      title
      ...EventBanner
    }
  }
`, [EventBannerFragment]);

const UpdateEventScorekeepingScore = graphql(/* GraphQL */ `
  mutation UpdateEventScorekeepingScore(
    $eventId: uuid!
    $teamId1: uuid!
    $teamId2: uuid!
    $score1: Float!
    $score2: Float!
    $status: event_status_enum
  ) {
    update_event_by_pk(
      pk_columns: { id: $eventId }
      _set: { status: $status }
    ) {
      id
      status
      event_clock {
        event_id
      }
    }
    team1: update_event_team_by_pk(
      pk_columns: { event_id: $eventId, team_id: $teamId1 }
      _set: { score: $score1 }
    ) {
      team_id
      event_id
      score
    }
    team2: update_event_team_by_pk(
      pk_columns: { event_id: $eventId, team_id: $teamId2 }
      _set: { score: $score2 }
    ) {
      team_id
      event_id
      score
    }
  }
`);

export default function EventScorekeepingScore() {
  const {
    handleSubmit,
    setValue,
    formState: { isLoading },
  } = useFormContext();
  const { dismiss } = useRouter();
  const { status } = useWatch<EventScorekeepingScoreFormValues>();

  const [, updateEventScorekeepingScore] = useMutation(
    UpdateEventScorekeepingScore,
  );

  const { eventId } = useLocalSearchParams<{ eventId?: string }>();

  const [{ data }] = useQuery({
    query: GetEventScorekeepingScore,
    variables: { eventId: eventId || '' },
  });

  const event = data?.event_by_pk;

  const gameBannerFragment = readFragment(
    EventBannerFragment,
    data?.event_by_pk,
  );

  const eventTeams = readFragment(
    EventBannerItemFragment,
    gameBannerFragment?.eventTeams,
  );

  const firstTeam = readFragment(EventBannerItemFragment, eventTeams?.[0]);
  const secondTeam = readFragment(EventBannerItemFragment, eventTeams?.[1]);

  const onSubmit = async (formValues: EventScorekeepingScoreFormValues) => {
    const teamId1 = gameBannerFragment?.eventTeams[0].team_id;
    const teamId2 = gameBannerFragment?.eventTeams[1].team_id;

    if (eventId && teamId1 && teamId2) {
      const score1 = formValues.scores?.[teamId1]
        ? Number.parseFloat(formValues.scores[teamId1])
        : 0;
      const score2 = formValues.scores?.[teamId2]
        ? Number.parseFloat(formValues.scores[teamId2])
        : 0;

      const { error } = await updateEventScorekeepingScore({
        eventId,
        teamId1,
        teamId2,
        score1,
        score2,
        status,
      });
      if (!error) {
        selectionAsync();
        dismiss();
      }
    }
  };

  return (
    <>
      <Stack.Screen options={{ headerTitle: event?.title || '' }} />
      <FormContentBoundary loading={isLoading}>
        <ModalKeyboardAwareScrollView
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="interactive"
        >
          <Box>
            <BackgroundContainer>
              <EventBannerBackground fragment={eventTeams?.[0]} />
              <EventBannerBackground fragment={eventTeams?.[1]} />
            </BackgroundContainer>
            <BackgroundFilter />
            <Box>
              <Box flex>
                <Box flex direction="horizontal">
                  <EventBannerItem
                    hideScore
                    fragment={eventTeams?.[0]}
                    index={0}
                  />
                  <EventScorekeepingScoreInput
                    id={firstTeam?.team.id || ''}
                    color={firstTeam?.team.home_primary_color || '#000'}
                    teamTitle={firstTeam?.team.title || 'First team'}
                  />
                </Box>
                <Box flex direction="horizontal">
                  <EventBannerItem
                    hideScore
                    fragment={eventTeams?.[1]}
                    index={1}
                  />
                  <EventScorekeepingScoreInput
                    id={secondTeam?.team.id || ''}
                    color={secondTeam?.team.home_primary_color || '#000'}
                    teamTitle={secondTeam?.team.title || 'Second team'}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          <EventStatusSelect
            selected={status ? [status] : []}
            onPress={(item) => setValue('status', item, { shouldDirty: true })}
          />
        </ModalKeyboardAwareScrollView>
        <ModalKeyboardStickyView>
          <FormSaveButton
            testID="EventScorekeepingScoreSaveButton"
            onPress={handleSubmit(onSubmit)}
            info={{ text: 'Save' }}
          />
        </ModalKeyboardStickyView>
      </FormContentBoundary>
    </>
  );
}
